import { type } from 'arktype'

import { type Amount, amountSchema, type FinancialRate, lt, newAmount, newFinancialRate } from '@orus.eu/amount'

/**
 * The limits and deductibles an underwriter may raise or lower from the back office. `DEFAULT` keeps the value of the
 * offer. The values are the labels ops tooling reads.
 */
export const frMrpaxeDerogationValues = {
  generalDeductible: ['DEFAULT', '1 000', '1 500'],
  stolenAssetsValue: ['DEFAULT', '10%', '20%', '75%', '100%'],
  cashAndValues: ['DEFAULT', '10 000', '15 000', '20 000'],
  brokenMaterial: ['DEFAULT', '10 000', '20 000', '30 000'],
  preservedGoods: ['DEFAULT', '5%', '10%', '20%'],
  optionPeValue: ['DEFAULT', '18 mois', '24 mois'],
  optionBdgValue: ['DEFAULT', '15 000', '20 000', '30 000'],
  outdoorFurniture: ['DEFAULT', '5 000', '10 000', '20 000'],
  realEstate: ['DEFAULT', '30 000', '40 000', '50 000'],
} as const

type FrMrpaxeDerogationValues = typeof frMrpaxeDerogationValues

export type FrMrpaxeDerogationGeneralDeductible = FrMrpaxeDerogationValues['generalDeductible'][number]
export type FrMrpaxeDerogationStolenAssetsValue = FrMrpaxeDerogationValues['stolenAssetsValue'][number]
export type FrMrpaxeDerogationCashAndValues = FrMrpaxeDerogationValues['cashAndValues'][number]
export type FrMrpaxeDerogationBrokenMaterial = FrMrpaxeDerogationValues['brokenMaterial'][number]
export type FrMrpaxeDerogationPreservedGoods = FrMrpaxeDerogationValues['preservedGoods'][number]
export type FrMrpaxeDerogationOptionPeValue = FrMrpaxeDerogationValues['optionPeValue'][number]
export type FrMrpaxeDerogationOptionBdgValue = FrMrpaxeDerogationValues['optionBdgValue'][number]
export type FrMrpaxeDerogationOutdoorFurniture = FrMrpaxeDerogationValues['outdoorFurniture'][number]
export type FrMrpaxeDerogationRealEstate = FrMrpaxeDerogationValues['realEstate'][number]

export const frMrpaxeDerogationAssetsValueType = type({ type: "'amount'", amount: amountSchema }).or({
  type: "'default'",
})
export type FrMrpaxeDerogationAssetsValue = typeof frMrpaxeDerogationAssetsValueType.infer

/** The highest contents value a customer can declare. Above it, only an underwriter's derogation insures the contents. */
export const FR_MRPAXE_MAX_ASSETS_VALUE = newAmount(250_000)

/** The contents value an underwriter may set instead of the declared one, above the 250,000 € subscription cap. */
export const FR_MRPAXE_MIN_DEROGATION_ASSETS_VALUE = newAmount(250_000)
export const FR_MRPAXE_MAX_DEROGATION_ASSETS_VALUE = newAmount(500_000)

const generalDeductibleAmounts: Record<FrMrpaxeDerogationGeneralDeductible, Amount> = {
  DEFAULT: newAmount(450),
  '1 000': newAmount(1_000),
  '1 500': newAmount(1_500),
}

const stolenAssetsRates: Record<FrMrpaxeDerogationStolenAssetsValue, FinancialRate> = {
  DEFAULT: newFinancialRate(0.5),
  '10%': newFinancialRate(0.1),
  '20%': newFinancialRate(0.2),
  '75%': newFinancialRate(0.75),
  '100%': newFinancialRate(1),
}

const cashAndValuesAmounts: Record<FrMrpaxeDerogationCashAndValues, Amount> = {
  DEFAULT: newAmount(3_000),
  '10 000': newAmount(10_000),
  '15 000': newAmount(15_000),
  '20 000': newAmount(20_000),
}

/** `DEFAULT` is absent: the default broken material limit depends on the BOOST option. */
const brokenMaterialAmounts: Record<Exclude<FrMrpaxeDerogationBrokenMaterial, 'DEFAULT'>, Amount> = {
  '10 000': newAmount(10_000),
  '20 000': newAmount(20_000),
  '30 000': newAmount(30_000),
}

const preservedGoodsRates: Record<FrMrpaxeDerogationPreservedGoods, FinancialRate> = {
  DEFAULT: newFinancialRate(0.15),
  '5%': newFinancialRate(0.05),
  '10%': newFinancialRate(0.1),
  '20%': newFinancialRate(0.2),
}

const optionPeMonths: Record<FrMrpaxeDerogationOptionPeValue, number> = {
  DEFAULT: 12,
  '18 mois': 18,
  '24 mois': 24,
}

const optionBdgAmounts: Record<FrMrpaxeDerogationOptionBdgValue, Amount> = {
  DEFAULT: newAmount(8_000),
  '15 000': newAmount(15_000),
  '20 000': newAmount(20_000),
  '30 000': newAmount(30_000),
}

const outdoorFurnitureAmounts: Record<FrMrpaxeDerogationOutdoorFurniture, Amount> = {
  DEFAULT: newAmount(3_500),
  '5 000': newAmount(5_000),
  '10 000': newAmount(10_000),
  '20 000': newAmount(20_000),
}

const realEstateAmounts: Record<FrMrpaxeDerogationRealEstate, Amount> = {
  DEFAULT: newAmount(20_000),
  '30 000': newAmount(30_000),
  '40 000': newAmount(40_000),
  '50 000': newAmount(50_000),
}

export function getFrMrpaxeGeneralDeductible(derogation: FrMrpaxeDerogationGeneralDeductible | undefined): Amount {
  return generalDeductibleAmounts[derogation ?? 'DEFAULT']
}

export type FrMrpaxeWorksLiabilityDeductible =
  | { type: 'tenPercentOfDamages'; minimum: Amount; maximum: Amount }
  | { type: 'generalDeductible'; amount: Amount }

/**
 * The works liability (RCT) deductible for one kind of damage: 10 % of the damages from the general deductible up to
 * `maximum`, or the general deductible alone once it reaches `maximum`, as the operating liability table words it.
 */
export function getFrMrpaxeWorksLiabilityDeductible(
  generalDeductible: Amount,
  maximum: Amount,
): FrMrpaxeWorksLiabilityDeductible {
  return lt(generalDeductible, maximum)
    ? { type: 'tenPercentOfDamages', minimum: generalDeductible, maximum }
    : { type: 'generalDeductible', amount: generalDeductible }
}

export function getFrMrpaxeStolenAssetsRate(
  derogation: FrMrpaxeDerogationStolenAssetsValue | undefined,
): FinancialRate {
  return stolenAssetsRates[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeCashAndValuesLimit(derogation: FrMrpaxeDerogationCashAndValues | undefined): Amount {
  return cashAndValuesAmounts[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeBrokenMaterialLimit(
  derogation: FrMrpaxeDerogationBrokenMaterial | undefined,
  isBoostActive: boolean,
): Amount {
  if (!derogation || derogation === 'DEFAULT') return newAmount(isBoostActive ? 5_000 : 3_000)
  return brokenMaterialAmounts[derogation]
}

export function getFrMrpaxePreservedGoodsRate(derogation: FrMrpaxeDerogationPreservedGoods | undefined): FinancialRate {
  return preservedGoodsRates[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeOptionPeMonths(derogation: FrMrpaxeDerogationOptionPeValue | undefined): number {
  return optionPeMonths[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeOptionBdgLimit(derogation: FrMrpaxeDerogationOptionBdgValue | undefined): Amount {
  return optionBdgAmounts[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeOutdoorFurnitureLimit(derogation: FrMrpaxeDerogationOutdoorFurniture | undefined): Amount {
  return outdoorFurnitureAmounts[derogation ?? 'DEFAULT']
}

export function getFrMrpaxeRealEstateLimit(derogation: FrMrpaxeDerogationRealEstate | undefined): Amount {
  return realEstateAmounts[derogation ?? 'DEFAULT']
}

/** The contents value the damage covers insure: the underwriter's derogation when there is one. */
export function getFrMrpaxeApplicableAssetsValue(
  assetsValue: Amount,
  derogation: FrMrpaxeDerogationAssetsValue | undefined,
): Amount {
  return derogation?.type === 'amount' ? derogation.amount : assetsValue
}
